/**
 * BodyMap — SVG silhueta clicável para marcar zonas de dor.
 * Usa coordenadas em viewBox 0 0 200 360.
 *
 * Cada zona tem um id que é guardado no array `value` (List<string>).
 */
const ZONES = [
  // FRONTAL (x=10..90)
  { id: "cabeca",      view: "front", cx: 50,  cy: 26,  r: 14, label: "Cabeça" },
  { id: "cervical",    view: "front", cx: 50,  cy: 50,  r: 7,  label: "Cervical" },
  { id: "ombro_e",     view: "front", cx: 30,  cy: 64,  r: 8,  label: "Ombro E." },
  { id: "ombro_d",     view: "front", cx: 70,  cy: 64,  r: 8,  label: "Ombro D." },
  { id: "torax",       view: "front", cx: 50,  cy: 95,  r: 10, label: "Tórax" },
  { id: "abdomen",     view: "front", cx: 50,  cy: 130, r: 10, label: "Abdómen" },
  { id: "anca_e",      view: "front", cx: 38,  cy: 168, r: 8,  label: "Anca E." },
  { id: "anca_d",      view: "front", cx: 62,  cy: 168, r: 8,  label: "Anca D." },
  { id: "joelho_e",    view: "front", cx: 38,  cy: 230, r: 7,  label: "Joelho E." },
  { id: "joelho_d",    view: "front", cx: 62,  cy: 230, r: 7,  label: "Joelho D." },
  { id: "tibiotarsica_e", view: "front", cx: 38, cy: 308, r: 6, label: "Tíbio-társica E." },
  { id: "tibiotarsica_d", view: "front", cx: 62, cy: 308, r: 6, label: "Tíbio-társica D." },
  // POSTERIOR (x=110..190)
  { id: "occipital",   view: "back", cx: 150, cy: 26,  r: 12, label: "Occipital" },
  { id: "trapezio",    view: "back", cx: 150, cy: 56,  r: 10, label: "Trapézio" },
  { id: "dorsal",      view: "back", cx: 150, cy: 92,  r: 12, label: "Dorsal" },
  { id: "lombar",      view: "back", cx: 150, cy: 138, r: 12, label: "Lombar" },
  { id: "sacro",       view: "back", cx: 150, cy: 168, r: 9,  label: "Sacro" },
  { id: "gluteo_e",    view: "back", cx: 138, cy: 188, r: 8,  label: "Glúteo E." },
  { id: "gluteo_d",    view: "back", cx: 162, cy: 188, r: 8,  label: "Glúteo D." },
  { id: "isquio_e",    view: "back", cx: 138, cy: 230, r: 7,  label: "Isquiotibiais E." },
  { id: "isquio_d",    view: "back", cx: 162, cy: 230, r: 7,  label: "Isquiotibiais D." },
  { id: "gemeos_e",    view: "back", cx: 138, cy: 280, r: 7,  label: "Gémeos E." },
  { id: "gemeos_d",    view: "back", cx: 162, cy: 280, r: 7,  label: "Gémeos D." },
];

const SILHOUETTE = "M0 0";

export default function BodyMap({ value = [], onChange, readOnly = false }) {
  const set = new Set(value);
  const toggle = (id) => {
    if (readOnly) return;
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange?.(Array.from(next));
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5" data-testid="body-map">
      <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Mapa de dor</div>
      <p className="text-[11px] text-slate-500 mt-1">Toca nas zonas onde o paciente sente desconforto · Vista frontal (esq.) e posterior (dir.)</p>

      <svg viewBox="0 0 200 360" className="w-full mt-4 select-none" style={{ maxHeight: "420px" }}>
        <defs>
          <radialGradient id="zone-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(244,63,94,0.55)" />
            <stop offset="100%" stopColor="rgba(244,63,94,0)" />
          </radialGradient>
          <linearGradient id="silh" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(56,189,248,0.18)" />
            <stop offset="1" stopColor="rgba(56,189,248,0.02)" />
          </linearGradient>
        </defs>

        {/* Silhuetas simples — frente e costas */}
        {[0, 100].map((dx, idx) => (
          <g key={idx} transform={`translate(${dx},0)`}>
            {/* Cabeça */}
            <ellipse cx="50" cy="26" rx="16" ry="20" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Pescoço */}
            <rect x="44" y="42" width="12" height="10" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Tronco */}
            <path d="M 25 56 Q 50 48 75 56 L 72 150 Q 50 158 28 150 Z"
                  fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Ancas */}
            <path d="M 30 150 Q 50 162 70 150 L 70 178 Q 50 184 30 178 Z"
                  fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Pernas */}
            <path d="M 32 178 L 36 320 L 44 320 L 46 178 Z" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            <path d="M 54 178 L 56 320 L 64 320 L 68 178 Z" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Pés */}
            <ellipse cx="40" cy="332" rx="10" ry="6" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            <ellipse cx="60" cy="332" rx="10" ry="6" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Braços */}
            <path d="M 22 60 L 16 150 L 24 152 L 30 60 Z" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            <path d="M 78 60 L 84 150 L 76 152 L 70 60 Z" fill="url(#silh)" stroke="rgba(148,163,184,0.45)" strokeWidth="0.6" />
            {/* Etiqueta */}
            <text x="50" y="354" textAnchor="middle" fontSize="7" fill="rgba(148,163,184,0.6)" fontFamily="ui-monospace, monospace">
              {idx === 0 ? "FRONTAL" : "POSTERIOR"}
            </text>
          </g>
        ))}

        {/* Zonas clicáveis */}
        {ZONES.map((z) => {
          const active = set.has(z.id);
          return (
            <g key={z.id}>
              {active && (
                <circle cx={z.cx} cy={z.cy} r={z.r + 2} fill="url(#zone-glow)" />
              )}
              <circle
                data-testid={`body-zone-${z.id}`}
                cx={z.cx} cy={z.cy} r={z.r}
                fill={active ? "rgba(244,63,94,0.85)" : "rgba(56,189,248,0.0)"}
                stroke={active ? "#f43f5e" : "rgba(125,211,252,0.5)"}
                strokeWidth={active ? 1.2 : 0.6}
                style={{
                  cursor: readOnly ? "default" : "pointer",
                  filter: active ? "drop-shadow(0 0 6px rgba(244,63,94,0.7))" : "none",
                  transition: "all .25s ease",
                }}
                onClick={() => toggle(z.id)}
              >
                <title>{z.label}</title>
              </circle>
            </g>
          );
        })}
      </svg>

      {value.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {value.map((id) => {
            const z = ZONES.find((x) => x.id === id);
            return (
              <span key={id} className="text-[10px] px-2 py-0.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-200">
                {z?.label || id}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const BODY_ZONES = ZONES;
